import { Link, useLocation } from "react-router-dom";
import { Grid3x3, FileText, Shield, ChevronRight, ChevronDown, Mail, ArrowRightLeft, Type, MessageSquareOff, ClipboardList, QrCode, Receipt, FileSpreadsheet, ImageDown, Code2, FilePlus, Combine } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const primaryNav = [
  { path: "/qr-generator", label: "QR Code", icon: QrCode },
  { path: "/invoice-generator", label: "Invoice", icon: Receipt },
  { path: "/json-formatter", label: "JSON Format", icon: Code2 },
];

const moreTools = [
  { path: "/pdf-to-excel", label: "PDF to Excel", icon: FileSpreadsheet },
  { path: "/docx-to-pdf", label: "DOCX to PDF", icon: FilePlus },
  { path: "/pdf-merger", label: "PDF Merger", icon: Combine },
  { path: "/image-compressor", label: "Image Compress", icon: ImageDown },
  { path: "/json-to-excel", label: "JSON to Excel", icon: ArrowRightLeft },
  { path: "/metadata-cleaner", label: "Metadata Cleaner", icon: Shield },
  { path: "/tracker-generator", label: "Tracker Sheet", icon: ClipboardList },
  { path: "/formula-explainer", label: "Formula Explainer", icon: FileText },
  { path: "/case-fixer", label: "Case Fixer", icon: Type },
  { path: "/leave-generator", label: "Leave Application", icon: Mail },
  { path: "/excuse-generator", label: "Meeting Excuse", icon: MessageSquareOff },
];

const allTools = [...primaryNav, ...moreTools];

export function Header() {
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/80 backdrop-blur-xl" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
            <div className="w-8 h-8 bg-[#0055FF] rounded-md flex items-center justify-center">
              <Grid3x3 className="w-4 h-4 text-white" />
            </div>
            <span className="text-xl font-black tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              ToolBox
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="main-nav">
            {primaryNav.map((link) => {
              const isActive = location.pathname === link.path;
              const Icon = link.icon;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.path.slice(1)}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "bg-[#0055FF] text-white" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {link.label}
                </Link>
              );
            })}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    moreTools.some(t => t.path === location.pathname)
                      ? "bg-[#0055FF] text-white"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  }`}
                  data-testid="more-tools-trigger"
                >
                  More Tools
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {moreTools.map((tool) => {
                  const Icon = tool.icon;
                  return (
                    <DropdownMenuItem key={tool.path} asChild>
                      <Link
                        to={tool.path}
                        className="flex items-center gap-2.5 cursor-pointer"
                        data-testid={`dropdown-${tool.path.slice(1)}`}
                      >
                        <Icon className="w-4 h-4 text-slate-500" />
                        {tool.label}
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Mobile Nav */}
          <nav className="flex lg:hidden items-center gap-1" data-testid="mobile-nav">
            {primaryNav.slice(0, 2).map((link) => {
              const isActive = location.pathname === link.path;
              const Icon = link.icon;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`p-2 rounded-md transition-colors ${isActive ? "bg-[#0055FF] text-white" : "text-slate-500 hover:bg-slate-100"}`}
                >
                  <Icon className="w-5 h-5" />
                </Link>
              );
            })}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-2 rounded-md text-slate-500 hover:bg-slate-100" data-testid="mobile-more-trigger">
                  <Grid3x3 className="w-5 h-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {allTools.map((tool) => {
                  const Icon = tool.icon;
                  return (
                    <DropdownMenuItem key={tool.path} asChild>
                      <Link to={tool.path} className="flex items-center gap-2.5 cursor-pointer">
                        <Icon className="w-4 h-4 text-slate-500" />
                        {tool.label}
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
        </div>
      </div>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-slate-200 bg-slate-50 mt-auto" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 bg-[#0055FF] rounded-md flex items-center justify-center">
                <Grid3x3 className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="text-lg font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>ToolBox</span>
            </div>
            <p className="text-sm text-slate-500 leading-relaxed max-w-xs">
              Free productivity tools for everyone. No sign-up. No tracking. Just paste and go.
            </p>
          </div>
          <div>
            <h4 className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-4">All Tools</h4>
            <div className="space-y-2">
              {allTools.map((link) => (
                <Link key={link.path} to={link.path} className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#0055FF] transition-colors">
                  <ChevronRight className="w-3 h-3" />{link.label}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-4">About</h4>
            <p className="text-sm text-slate-500 leading-relaxed">
              ToolBox provides free, browser-based productivity tools. Generate QR codes, create invoices, convert files, and more.
            </p>
          </div>
        </div>
        <div className="border-t border-slate-200 mt-8 pt-6 text-center">
          <p className="text-xs text-slate-400">&copy; {new Date().getFullYear()} ToolBox. All tools are free to use.</p>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
